import {useEffect, useMemo, useRef} from "react";
import {useConfigContext} from "@/ConfigProvider";
import {ListUpdater} from "@/ListUpdater";
import {useAutocomplete} from "@/autocomplete/context/GlobalContext";
import {Panel} from "@/autocomplete/components/Panel";
import {ProductList} from "@/autocomplete/components/ProductList";
import {ProductTile} from "@/autocomplete/components/ProductTile";
import {ShowAllButton} from "@/autocomplete/components/ShowAllButton";
import {useAnalytics} from "@/providers/AnalyticsProvider";
import {MIN_QUERY_LENGTH} from "@/utils/searchConfig";
export function Products() {
    const {autocompleteState: {collections, query, status}, autocomplete} = useAutocomplete();
    const {tc, algoliaConfig: {siteCode, languageCode, autocompleteProductListView}} = useConfigContext();
    const {trackSearch, trackViewItemList} = useAnalytics();
    const listRef = useRef<HTMLOListElement>(null);
    const items = useMemo(() => {
        const productCollections = collections.filter(({source}) => source.sourceId === "products");
        return productCollections[0]?.items || [];
    }, [collections]);
    useEffect(() => {
        if (!listRef.current || items.length === 0) return;

        const id = requestAnimationFrame(() => {
            ListUpdater(listRef.current!, siteCode, languageCode, { allowPreHydration: true });

            setTimeout(() => {
                if (query && query.trim()) {
                    trackSearch(query.trim(), items.length);
                    trackViewItemList(items, true);
                }
            }, 100);
        });
        return () => cancelAnimationFrame(id);
    }, [items, siteCode, languageCode, query, trackSearch, trackViewItemList]);
    // No search has run yet below the minimum query length (see GlobalContext's
    // getSources gating), so don't show a "no results" message for it either.
    if (!query || query.length < MIN_QUERY_LENGTH) return <></>;
    if (items.length === 0 && status !== "loading") return <NoEntries/>;

    return (
        <Panel title={tc.products} footer={<ShowAllButton/>}>
            <ol
                className="algoliaAutocompleteProducts itemlist"
                ref={listRef}
                {...autocomplete.getListProps()}
            >
                {items.map((item) => (
                    autocompleteProductListView
                        ? <ProductList
                            key={item.objectID}
                            hit={item}
                            postFixLink={encodeURIComponent(query)}
                        />
                        : <ProductTile
                            key={item.objectID}
                            hit={item}
                            postFixLink={encodeURIComponent(query)}
                        />
                ))}
            </ol>
        </Panel>
    );
}

function NoEntries() {
    const {autocompleteState: {query}} = useAutocomplete();
    const {tc} = useConfigContext();
    return <div className="emptybox">{tc.noEntriesFoundForSearch} <q>{query}</q>.</div>;
}
